/**
 * The server answers in English ({ msg: "Order not found" }). Screens show
 * that msg as is, in ~46 places, so it is translated once, where every reply
 * arrives (src/api.ts): an exact English match to a "serverMsg.*" line in
 * en.json becomes that line in the viewer's language. Lines with {slots}
 * match by pattern ("Minimum tip is {formatAmount}." ← "Minimum tip is €2.").
 *
 * A message with no line here passes through in English, exactly as before.
 * The English original stays on the reply as msgEn for code that compares
 * the wording (analytics, the "Email already in use" sign-up hint).
 */
import en from "./locales/en.json";
import { currentLanguage, t, MessageKey } from "./index";

const PREFIX = "serverMsg.";

type Pattern = { key: MessageKey; re: RegExp; slots: string[]; fixed: number };

let exact: Map<string, MessageKey> | null = null;
let patterns: Pattern[] = [];

const escape = (s: string) => s.replace(/[.*+?^$()|[\]\\]/g, "\\$&");

function build() {
  exact = new Map();
  patterns = [];
  for (const [k, v] of Object.entries(en as Record<string, string>)) {
    if (!k.startsWith(PREFIX)) continue;
    const key = k as MessageKey;
    const slots = Array.from(v.matchAll(/\{(\w+)\}/g), (m) => m[1]);
    if (!slots.length) exact.set(v, key);
    else patterns.push({ key, slots, re: new RegExp(`^${escape(v).replace(/\\?\{\w+\\?\}/g, "(.+?)")}$`), fixed: v.replace(/\{\w+\}/g, "").length });
  }
  // Most fixed text first: "…the limit is {max} per setlist." before "…the limit is {max}.",
  // which would otherwise swallow " per setlist" into its slot.
  patterns.sort((a, b) => b.fixed - a.fixed);
}

/** The server's English message in the viewer's language (unknown messages stay English). */
export function localizeServerMessage(text: string): string {
  if (currentLanguage() === "en") return text;
  if (!exact) build();
  const key = exact!.get(text);
  if (key) return t(key);
  for (const p of patterns) {
    const m = p.re.exec(text);
    if (m) return t(p.key, Object.fromEntries(p.slots.map((s, i) => [s, m[i + 1]])));
  }
  return text;
}

/**
 * Translate a reply body in place: msg (and error, the older field). The
 * English stays as msgEn / errorEn; a body already done (a retried request
 * passes the interceptors twice) is left alone.
 */
export function localizeReplyBody(data: unknown) {
  if (!data || typeof data !== "object" || currentLanguage() === "en") return;
  const body = data as Record<string, unknown>;
  for (const field of ["msg", "error"]) {
    const value = body[field];
    if (typeof value === "string" && !(`${field}En` in body)) {
      body[`${field}En`] = value;
      body[field] = localizeServerMessage(value);
    }
  }
}
